<template>
  <div v-show="showWithinBoundary">
    <div
      v-if="'top' === togglePosition && enableToggleButton"
      @click="toggle"
      class="cursor-pointer"
      :key="count"
    >
      <slot
        name="toggle"
        :expanded="expandedState"
      ></slot>
    </div>
    <div
      ref="collapsible"
      class="block relative !overflow-y-hidden"
      :key="count"
    >
      <span
        ref="background"
        class="hidden absolute left-0 right-0 bottom-0 h-full max-h-4xs bg-cover bg-gradient-to-t from-white to-transparent"
      ></span>
      <slot name="collapsibleContent"/>
    </div>
    <div
      v-if="'bottom' === togglePosition && enableToggleButton"
      @click="toggle"
      class="cursor-pointer"
      :key="count"
    >
      <slot
        name="toggle"
        :expanded="expandedState"
      ></slot>
    </div>
  </div>
  <slot v-if="!showWithinBoundary" name="collapsibleContent"/>
</template>

<script>
import Breakpoint from '../../js/util/breakpoint';

export default {
  name: "adivare-collapse",
  props: {
    // show pagination at defined breakpoint.
    // Default is enabled at all breakpoints.
    // Allowed values: '<sm', '<md', '<lg', '<xl', '>sm', '>md', '>lg' and '>xl'
    breakpoint: {
      type: String,
      default: null,
      required: false,
      validator: (value) => {
        return [
          '<sm',
          '<md',
          '<lg',
          '<xl',
          '>sm',
          '>md',
          '>lg',
          '>xl',
        ].includes(value);
      }
    },
    // speed of collapsible item
    speed: {
      type: Number,
      default: 3,
      required: false,
    },
    // calculated max-height of collapsible-item can be expanded with offset (max-height + offset)
    // useful when you expect the height in the collapsible-item to dynamically change during whatever interaction within the collapsible-item.
    offset: {
      type: Number,
      default: 0,
      required: false,
    },
    expanded: {
      type: Boolean,
      default: false,
      required: false,
    },
    // add toggle in top or bottom of collapsible content
    togglePosition: {
      type: String,
      default: 'top',
      required: false,
      validator: value => ['top', 'bottom'].includes(value)
    },
    // only make content collapsible when collapsible content is more than 'collapseAtHeight' in pixels.
    collapseAtHeight: {
      type: Number,
      default: 0,
      required: false
    }
  },
  data() {
    return {
      scrollHeight: null,
      expandedState: this.expanded,
      showWithinBoundary: true,
      enableToggleButton: false,
      count: 0,
    }
  },
  watch: {
    expanded: {
      handler(state) {
        this.expandedState = state;
        true === state ? this.expand(this.$refs.collapsible) : this.collapse(this.$refs.collapsible);
      },
    },
  },
  methods: {
    enableToggle() {
      this.enableToggleButton = this.collapseAtHeight < this.getScrollHeight();
    },
    initialExpandOrCollapse() {
      if (this.collapseAtHeight > this.getScrollHeight()) return;
      true === this.expanded ? this.expand(this.$refs.collapsible) : this.collapse(this.$refs.collapsible);
    },
    toggle() {
      const maxHeight = this.$refs.collapsible.style.maxHeight;
      (`${this.collapseAtHeight || 0}px` === maxHeight || '' === maxHeight)
        ? this.expand(this.$refs.collapsible)
        : this.collapse(this.$refs.collapsible);
    },
    getScrollHeight() {
      return (this.$refs.collapsible?.scrollHeight ?? 0) + this.offset;
    },
    expand() {
      const scrollHeight = this.getScrollHeight();
      this.$refs.collapsible.style.cssText += `
        transition: all ${(Math.round(scrollHeight * (1 / this.speed) * 0.3) / 100)}s linear;
        max-height: ${scrollHeight + 20}px;
        opacity: 1;
        pointer-events: auto;
        overflow: unset;
      `;
      this.$refs.background.style.display = 'none';
      this.expandedState = true;
    },
    collapse() {
      this.$refs.collapsible.style.cssText += `
        pointer-events: none;
        overflow: hidden;
      `;
      if (null === this.collapseAtHeight) {
        this.$refs.collapsible.style.cssText += `
          opacity: 0;
          max-height: 0px;
        `;
      } else {
        this.$refs.collapsible.style.cssText += `
          max-height: ${this.collapseAtHeight}px
        `;
        this.$refs.background.style.display = 'block';
      }
      this.expandedState = false;
    },
    setBreakpointActivator() {
      if ('none' === this.breakpoint) {
        this.showWithinBoundary = true;
      } else {
        this.showWithinBoundary = Breakpoint.isActive(this.breakpoint);
        Breakpoint.setListener({
          breakpoint: this.breakpoint,
          eventName: `collapse${this.breakpoint}`,
        });
        window.addEventListener(`collapse${this.breakpoint}`, event => {
          this.showWithinBoundary = event.detail.isActive;
        });
      }
    },
    isElement(obj) {
      return obj instanceof Element || obj instanceof HTMLElement;
    }
  },
  mounted() {
    if (true === this.isElement(this.$refs.collapsible)) {
      this.initialExpandOrCollapse();
      this.enableToggle();
      this.setBreakpointActivator();
    }
  }
}
</script>
